/**
 * The passives panel's rules, extracted so they can be tested without rendering —
 * the same split `ui/skills.ts` has, for the same reason.
 *
 * Unlike the skills panel, this one does **not** have to merge a trimmed frame onto
 * a catalogue: the server sends state for every node, because the pool is thirty
 * entries rather than a hundred. What it does have to do is turn the server's
 * `blocked` key into English, group by category, and describe a node's effect from
 * stat keys — the wording is the client's, the same division `display_template`
 * draws on the other side of the wire.
 */

import type { Attributes, PassiveBlocked, PassiveInfo, PassiveState } from '../net/protocol';
import { isPercentageStat, statNoun } from './compare';
import { shortfalls, type GateShortfall } from './skills';

/** What the panel needs to know about one node. */
export interface PassiveRow {
  def: PassiveInfo;
  /** Ranks already spent. */
  ranks: number;
  /** True when one more point could go in right now — the server's own answer. */
  available: boolean;
  /** Why not, when `available` is false. */
  blocked?: PassiveBlocked;
  /** Ranks spent is at the node's maximum. */
  maxed: boolean;
  /** Which attributes fall short of the gate. Empty when it is met. */
  missing: readonly GateShortfall[];
  /** No CHARACTER frame has arrived, so nothing is known about this node yet. */
  dormant: boolean;
}

/**
 * Builds a row per node, grouped by the catalogue's category order.
 *
 * The catalogue arrives in file order, which is already grouped by category and
 * sorted by requirement inside each — so the panel reads the way the file reads and
 * a designer's arrangement is the player's arrangement. Nothing is re-sorted here
 * on purpose: a "reachable first" ordering like the skills panel's would shuffle
 * nodes between renders as the character levels, and thirty rows do not need it.
 */
export function passiveRows(
  catalogue: readonly PassiveInfo[],
  states: readonly PassiveState[],
  total: Attributes | null,
): PassiveRow[] {
  const byId = new Map(states.map((s) => [s.passiveId, s]));

  return catalogue.map((def) => {
    const state = byId.get(def.passiveId);
    const ranks = state?.ranks ?? 0;

    return {
      def,
      ranks,
      available: state?.available ?? false,
      blocked: state?.blocked,
      maxed: ranks >= def.maxRank,
      missing: shortfalls(def.statGates, total),
      dormant: state === undefined,
    };
  });
}

/** The categories present in a catalogue, in its own order. */
export function passiveCategories(catalogue: readonly PassiveInfo[]): string[] {
  const seen: string[] = [];
  for (const def of catalogue) {
    if (!seen.includes(def.category)) seen.push(def.category);
  }
  return seen;
}

/** Total ranks spent across the pool, for the panel's summary line. */
export function ranksSpent(rows: readonly PassiveRow[]): number {
  return rows.reduce((sum, row) => sum + row.ranks, 0);
}


/**
 * One stat line: "+3% Physical damage per rank", or the total at the current rank.
 *
 * Showing the per-rank figure rather than the total is what makes a rank a decision
 * — the player is choosing whether to buy another one, and the total is on their
 * character sheet already.
 */
export function statLine(stat: string, perRank: number): string {
  const sign = perRank < 0 ? '−' : '+';
  const magnitude = Math.abs(perRank);
  // The noun and the unit come from `compare.ts` — **one table for the whole vocabulary**,
  // walked against `config.StatKeys` so a stat with no wording fails a test rather than
  // reaching a player as `crit_damage_pct`. This file used to keep its own shorter copy.
  const unit = isPercentageStat(stat) ? '%' : '';
  return `${sign}${magnitude}${unit} ${statNoun(stat)}`;
}

/** Every stat line for a node, in the order the server sent them. */
export function effectLines(def: PassiveInfo): string[] {
  return def.statsPerRank.map((s) => statLine(s.stat, s.perRank));
}

/**
 * Why a node cannot take a point, as something the player can act on.
 *
 * "Blocked" is a fact; "20 STR — you have 14" is a plan. This is the wording the
 * server deliberately does not own: it sends a key precisely so the HUD can say it
 * in terms of what the player is looking at.
 */
export function blockedText(row: PassiveRow): string {
  switch (row.blocked) {
    case 'MAX_RANK':
      return `Fully ranked at ${row.def.maxRank}`;
    case 'LEVEL':
      return `Needs level ${row.def.levelReq}`;
    case 'ATTRIBUTES':
      return row.missing.length > 0
        ? `Needs ${row.missing.map((m) => `${m.need} ${m.attribute} — you have ${m.have}`).join(', ')}`
        : `Needs ${gateSummary(row.def)}`;
    case 'NO_POINTS':
      return 'No passive points to spend';
    default:
      return '';
  }
}

/** "20 STR · 20 END", or "No requirement". */
export function gateSummary(def: PassiveInfo): string {
  const parts: string[] = [];
  for (const [key, label] of [
    ['str', 'STR'],
    ['dex', 'DEX'],
    ['int', 'INT'],
    ['end', 'END'],
  ] as const) {
    const need = def.statGates[key];
    if (need > 0) parts.push(`${need} ${label}`);
  }
  if (def.levelReq > 1) parts.unshift(`Level ${def.levelReq}`);
  return parts.length > 0 ? parts.join(' · ') : 'No requirement';
}
